/**
 * VI 色主题同步工具
 * 将 hex 颜色同步更新到 CSS 变量，使 Tailwind 组件响应租户 VI 色变化
 *
 * 数据流：
 * tenant.info.theme.maincolor (hex) -> handleChangeTheme (app.tsx)
 *   -> MUI createTheme (palette.primary.main)
 *   -> updateCSSVariables (本模块) -> :root CSS 变量 -> Tailwind 组件自动更新
 */

/**
 * Hex 颜色转 HSL
 * @param hex - 如 "#007aff" 或 "007aff"
 * @returns { h, s, l } - h: 0-360, s: 0-100, l: 0-100
 */
export function hexToHSL(hex: string): { h: number; s: number; l: number } {
  hex = hex.replace(/^#/, '');

  // 支持 3 位缩写
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      break;
    case g:
      h = ((b - r) / d + 2) / 6;
      break;
    case b:
      h = ((r - g) / d + 4) / 6;
      break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * 根据 hex 主色更新 :root 上所有主题相关 CSS 变量
 * 所有使用 hsl(var(--primary)) 等的 Tailwind 组件将自动更新
 *
 * @param hex - 主色 hex 值，如 "#007aff"
 */
export function updateCSSVariables(hex: string): void {
  if (!hex || typeof hex !== 'string') return;

  const hsl = hexToHSL(hex);
  const root = document.documentElement;
  const primary = `${hsl.h} ${hsl.s}% ${hsl.l}%`;

  // 主色
  root.style.setProperty('--primary', primary);
  root.style.setProperty('--primary-foreground', '0 0% 100%');

  // Ring（聚焦轮廓色）
  root.style.setProperty('--ring', primary);

  // Accent（主色的极浅版，用于 hover/选中背景）
  root.style.setProperty('--accent', `${hsl.h} ${hsl.s}% 97%`);
  root.style.setProperty('--accent-foreground', primary);

  // Sidebar 色系
  root.style.setProperty('--sidebar-primary', primary);
  root.style.setProperty('--sidebar-primary-foreground', '0 0% 100%');
  root.style.setProperty('--sidebar-accent', `${hsl.h} ${hsl.s}% 97%`);
  root.style.setProperty('--sidebar-accent-foreground', primary);
  root.style.setProperty('--sidebar-ring', primary);

  // Admin 图标色
  root.style.setProperty('--admin-icon-bg', `${hsl.h} ${hsl.s}% 95%`);
  root.style.setProperty('--admin-icon-color', primary);
  root.style.setProperty('--admin-chart-bar', `${hsl.h} ${hsl.s}% 85%`);
}
